import styled, { css } from 'styled-components'

import { colors } from 'app/theme/colors'
import { breakpoint, radius, space } from 'app/theme/tokens'

/**
 * Shared styled-components for the SummaryBlock cards on both Parent and
 * Teacher dashboards. The two dashboards previously kept byte-identical
 * copies of these (sans LongWrapper, which only ParentDashboard renders) —
 * now centralised here. Future visual tweaks to a dashboard card propagate
 * to both surfaces automatically.
 */

const summaryShadow = '10px 10px 32px -8px rgb(0 0 0 / 20%)'

const cardBase = css`
  border-radius: ${radius.md};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: ${space.md};
  transition: all 0.5s ease;
  box-shadow: ${summaryShadow};
  background: ${colors.white};
  gap: ${space.md};

  &:hover {
    box-shadow: unset;
  }

  ${(props) =>
    props.isSelected &&
    css`
      border: 1px solid ${colors.ohOrange};
    `}
`

export const Wrapper = styled.div`
  width: 264px;
  height: 226px;
  ${cardBase}

  /* Opt-in escape hatch for a card whose content is genuinely variable in
     length. The e-mandate card lists one row per unsettled booking, and a
     parent can hold four; at a fixed 226px those rows render past the card's
     bottom edge and are clipped, so the warning the card exists to raise is
     the part that disappears. Nothing else passes $grow, so every other card
     keeps the fixed height the grid is tuned for. */
  ${(props) =>
    props.$grow &&
    css`
      height: auto;
      min-height: 226px;
    `}

  /* Mobile: take the full width of the page and let height grow with
     content rather than being clipped at 226px. Keeps the layout from
     trying to fit two 264px cards side by side on a 375px-wide screen. */
  ${breakpoint.down('tablet')} {
    width: 100%;
    height: auto;
    min-height: 180px;
  }
`

export const LongWrapper = styled.div`
  width: 100%;
  min-height: 226px;
  ${cardBase}

  ${breakpoint.down('tablet')} {
    min-height: 0;
  }
`

export const Body = styled.div`
  height: 100%;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
`

export const Content = styled.div`
  font-size: 12px;
  text-overflow: ellipsis;
  overflow: hidden;
  text-transform: lowercase;
  ${(props) =>
    props.color &&
    props.plan &&
    css`
      font-family: AvenirDemi;
      font-size: 40px;
      color: ${props.color};
    `}

  ${(props) =>
    props.subheading &&
    props.type &&
    css`
      font-family: AvenirMedium;
      font-size: 14px;
      color: ${props.type === 'light'
        ? colors.textTertiary
        : colors.textPrimary};
    `}
`

export const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

export const CardName = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`

export const Icon = styled.img`
  height: 16px;
  width: 16px;
`

export const Heading = styled.div`
  font-family: AvenirMedium;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 2px;
`

export const InfoBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

export const Coins = styled.div`
  font-size: 16px;
  font-family: AvenirMedium;
  line-height: 20px;
  color: ${colors.black};
`

export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

/**
 * Content styled-component used by ParentDashboard SummaryBlock's
 * individual card sub-components (Attendance, Children, ClassroomExperience,
 * Engagement, EnrolmentExtension, Renewals, Wallet, WalletCancel). Each
 * previously kept its own byte-identical 50-line copy — extracted here so
 * future visual tweaks ship everywhere at once.
 *
 * Sub-components import this and re-export it as `Content` from their local
 * styles.js, keeping the `import * as styles from './styles'` call sites
 * unchanged.
 *
 * Variants for prop combinations:
 *   plan       → 40px AvenirDemi (the big number / coin value)
 *   subheading → 12px AvenirMedium
 *   heading    → 14px AvenirMedium
 *   regular    → 12px Avenir
 *   type       → light | medium | (default) → textTertiary | Secondary | Primary
 *   color      → arbitrary colour override (escape hatch — try not to use)
 */
export const SubBlockContent = styled.div`
  font-size: 16px;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${colors.textPrimary};

  ${(props) =>
    props.plan &&
    css`
      font-family: AvenirDemi;
      font-size: 40px;
    `}

  ${(props) =>
    props.subheading &&
    css`
      font-family: AvenirMedium;
      font-size: 12px;
    `}

  ${(props) =>
    props.heading &&
    css`
      font-family: AvenirMedium;
      font-size: 14px;
    `}

  ${(props) =>
    props.regular &&
    css`
      font-family: Avenir;
      font-size: 12px;
    `}

  ${(props) =>
    props.type &&
    css`
      color: ${props.type === 'light'
        ? colors.textTertiary
        : props.type === 'medium'
        ? colors.textSecondary
        : colors.textPrimary};
    `}

  ${(props) =>
    props.color &&
    css`
      color: ${props.color};
    `}
`
